import _ from 'lodash';
import moment from 'moment';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CAPTURE_MODE, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import {
  KEY_RECOIL,
  TYPE_BEAT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEventV2,
} from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  checkAndProcessBeatData,
  countBeatChannels,
  generateUpdatedBeatsV2,
  handleUpdateBeatChanges,
} from '@/entities/beat';
import { handleAddEventOthersV2 } from '@/entities/event';
import { checkAndProcessEcgData, formatDrawDisplay, formatDrawMutation } from '@/entities/ecg';
import { logError } from '@/shared/lib/apiHelpers';
import { TPickDateAction } from '@/shared/model';
import { getUpdatedBeatMarkerV2, handleResetAppliedBuldBeats } from '@/widgets/beats/ui/helper';
import {
  selectAiChannel,
  selectEcgObject,
  selectIsActiveTab,
  selectJumpDate,
  selectBeatChanges,
  selectEventNews,
  selectEventOthers,
  selectPickDateAction,
  selectPickedBookmarkDate,
  selectPickedEndDate,
  selectPickedStartDate,
  selectReloadEcgViewer,
  selectIsLoadingStrip,
  selectSelectedStrip,
  selectBulkBeatsChanges,
  selectResetBulkBeatsChanges,
  useReportStore,
} from '@/entities/report';
import { calculateNewRange } from './handler';
import type { BeatEcgViewerWraperProps } from './beatEcgViewerWrapper';

/**
 * Drives the ten-second ECG viewer on the RR-interval beat tab, owning strip download, beat-marker editing, date picking and bookmark markers for the TAB_1 report slice. Fetches ECG and beat data around the selected beat, rebuilds the viewer object when beat edits, bulk changes or viewer config move, overlays overlapping rhythm events and bookmarks, escapes date picking on keyup, and re-centres the window when a jump date falls outside the loaded strip.
 *
 * @param props - The wrapper inputs carrying the ECG data map, timezone offset and the callback that loads heatmap events for a list of dates.
 * @returns The viewer state and handlers: loading flags, the ECG object, strip refs, navigation, capture, pick-date and beat-update callbacks.
 */
const useBeatEcgViewerWrapper = ({
  ecgDataMap,
  timezoneOffset,
  callbackListDate,
}: Pick<BeatEcgViewerWraperProps, 'ecgDataMap' | 'timezoneOffset' | 'callbackListDate'>) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark; this file
  // reads/writes beat-only fields (id) that the union permits, but callers treat it as `any` shape - pre-existing gap.
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil)) as any;
  /**
   * Reads the current beat-type edit list for the RR-interval tab straight from the report store rather than from a subscribed value. Calls the beat-changes selector bound to the TAB_1 key against a fresh store snapshot, so callers inside async work see the latest edits.
   *
   * @returns The array of pending beat changes recorded for the tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Writes the tab's pending beat-type changes into the report store. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The beat change list or updater function to store.
   * @returns Nothing.
   */
  const setBeatChanges = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(keyRecoil, v);
  const aiChannel = useReportStore(selectAiChannel(keyRecoil));
  const setAiChannelAction = useReportStore((s) => s.actions.setAiChannel);
  /**
   * Writes the detected AI beat channel for the RR-interval tab into the report store. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The channel value or updater function to store.
   * @returns Nothing.
   */
  const setAiChannel = (v: Parameters<typeof setAiChannelAction>[1]) =>
    setAiChannelAction(keyRecoil, v);
  const reloadEcgViewer = useReportStore(selectReloadEcgViewer(keyRecoil));
  const loadingBeatStatus = useReportStore((s) => s.loadingBeatStatus);
  const ecgObject = useReportStore(selectEcgObject(keyRecoil));
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Publishes the strip payload the ECG viewer renders for the RR-interval tab. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The ECG object or updater function to store.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const combinedEventsInHeatmap = useReportStore((s) => s.combinedEventsInHeatmap);
  const pickDateAction = useReportStore(selectPickDateAction(keyRecoil));
  const setPickDateActionAction = useReportStore((s) => s.actions.setPickDateAction);
  /**
   * Sets which date the user is currently picking on the strip, or clears picking entirely. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The pick-date action or updater function to store.
   * @returns Nothing.
   */
  const setPickDateAction = (v: Parameters<typeof setPickDateActionAction>[1]) =>
    setPickDateActionAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const setPickedStartDateAction = useReportStore((s) => s.actions.setPickedStartDate);
  /**
   * Stores the strip timestamp picked as the start of a new event. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The start date or updater function to store.
   * @returns Nothing.
   */
  const setPickedStartDate = (v: Parameters<typeof setPickedStartDateAction>[1]) =>
    setPickedStartDateAction(keyRecoil, v);
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const setPickedEndDateAction = useReportStore((s) => s.actions.setPickedEndDate);
  /**
   * Stores the strip timestamp picked as the end of a new event. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The end date or updater function to store.
   * @returns Nothing.
   */
  const setPickedEndDate = (v: Parameters<typeof setPickedEndDateAction>[1]) =>
    setPickedEndDateAction(keyRecoil, v);
  const pickedBookmarkDate = useReportStore(selectPickedBookmarkDate(keyRecoil));
  const setPickedBookmarkDateAction = useReportStore((s) => s.actions.setPickedBookmarkDate);
  /**
   * Stores the strip timestamp picked for a new ECG bookmark. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The bookmark date or updater function to store.
   * @returns Nothing.
   */
  const setPickedBookmarkDate = (v: Parameters<typeof setPickedBookmarkDateAction>[1]) =>
    setPickedBookmarkDateAction(keyRecoil, v);
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  /**
   * Reads the manually drawn other-event annotations for the RR-interval tab without subscribing to them. Applies the event-others selector for the TAB_1 key to a fresh store snapshot so capture handlers append to the latest list.
   *
   * @returns The array of other events currently stored for the tab.
   */
  const getEventOthers = () => selectEventOthers(keyRecoil)(useReportStore.getState());
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Writes the tab's manually drawn other-event annotations into the report store. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The other-event list or updater function to store.
   * @returns Nothing.
   */
  const setEventOthers = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(keyRecoil, v);
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets or clears the timestamp the tab should jump the ECG window to. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The jump date or updater function to store.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets the fractional position within the strip that a jump should land on. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The jump portion value or updater function to store.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the tab's event option snapshot used by undo and redo into the report store. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The event options value or updater function to store.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const otherEvents = useReportStore(selectEventOthers(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the tab's beat option snapshot, the payload undo and redo replay, into the report store. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The beat options value or updater function to store.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  const bulkBeatsChanges = useReportStore(selectBulkBeatsChanges(keyRecoil));
  /**
   * Reads the pending bulk beat-type changes for the RR-interval tab outside of any React subscription. Runs the bulk-beats selector for the TAB_1 key against the current store snapshot so long-running handlers do not close over a stale value.
   *
   * @returns The bulk beat changes currently held in the store for the tab.
   */
  const getBulkBeatsChanges = () => selectBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads which previously applied bulk beat changes have been reverted on the RR-interval tab. Evaluates the reset-bulk-beats selector for the TAB_1 key against the live store snapshot instead of a render-time value.
   *
   * @returns The reset bulk beat changes currently stored for the tab.
   */
  const getResetBulkBeatsChanges = () =>
    selectResetBulkBeatsChanges(keyRecoil)(useReportStore.getState());
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Records which applied bulk beat changes have been reverted for the tab. Forwards the value or updater to the store action with the TAB_1 key already bound.
   *
   * @param v - The reset bulk beat changes or updater function to store.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(keyRecoil, v);
  const resetBulkBeatsChanges = useReportStore(selectResetBulkBeatsChanges(keyRecoil));
  const beatChanges = useReportStore(selectBeatChanges(keyRecoil));

  const startStripEpoch = useRef<any>(null);
  const stopStripEpoch = useRef<any>(null);
  const originalBeatMarkers = useRef<any[]>([]);
  const originalHesBeatStatus = useRef<any[]>([]);
  const originalBeatHeatMapIds = useRef<any[]>([]);
  const originalBeatTemplateIds = useRef<any[]>([]);
  const beatMarkersInEcgViewer = useRef<any[]>([]);
  const hesBeatStatusInEcgViewer = useRef<any[]>([]);
  const selectedStripBeatId = useRef<any>(null);
  const ecgChartRef = useRef<any>();
  const ecgObjectRef = useRef<any>();
  const currentBeatsChangeRef = useRef<any>();
  const currentRrResetChangeRef = useRef<any>();

  const [isLoading, setIsLoading] = useState(false);
  const [displayedBeatId, setDisplayedBeatId] = useState<any>(null);
  const currentEcgViewerConfig = useRef({ ...ECG_VIEWER_CONFIG_STORE });

  const isPickingDate =
    pickDateAction === TPickDateAction.START ||
    pickDateAction === TPickDateAction.END ||
    pickDateAction === TPickDateAction.BOOKMARK;

  //* prevent api race condition by not setState data of old selected strip or event
  let isActiveQueryBeat: boolean;

  /**
   * Converts a rhythm event into the sample positions and colour the ECG viewer needs to shade it over the current strip. Treats an entry without both start and stop as a minute-info row and leaves the positions at zero, otherwise it maps the epoch offsets from the strip start onto samples using the sampling frequency and picks gray for artifact, orange for AFib and red for everything else, clamping a negative stop to zero.
   *
   * @param event - The rhythm event or minute-info entry to place on the displayed strip.
   * @returns An object carrying startPos, stopPos, color and the event id.
   */
  const getEventInfoInEcgViewer = (event: any) => {
    const { samplingFrequency } = ecgDataMap;
    const isEvent = event.start && event.stop; // not minute info (minute info dont have start and stop)
    let startPos = 0;
    let stopPos = 0;
    let color;
    if (isEvent) {
      const isArtifactEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT;
      const isAfibEvent = event.type === TYPE_RHYTHM_EVENT_ENUM.AFIB;
      color = isArtifactEvent ? 'gray' : isAfibEvent ? 'orange' : 'red';
      const startEpoch = new Date(event.start).getTime();
      const stopEpoch = new Date(event.stop).getTime();
      const eventStartDiffSeconds = (startEpoch - startStripEpoch.current) / 1000;
      startPos = Math.round(eventStartDiffSeconds * samplingFrequency);
      const eventStopDiffSeconds = (stopEpoch - startStripEpoch.current) / 1000;
      stopPos = eventStopDiffSeconds > 0 ? Math.round(eventStopDiffSeconds * samplingFrequency) : 0;
    }
    return {
      startPos,
      stopPos,
      color,
      id: event.id,
    };
  };

  /**
   * Builds the viewer payload for a freshly downloaded strip and publishes it to the report store. Replays stored beat edits, bulk changes and resets onto the downloaded beats, records the resulting markers on refs, maps the overlapping rhythm events to sample positions, and assembles gain, channel, filter, speed and amplitude settings from the shared viewer config alongside the export copy of the ECG.
   *
   * @param ecg - The decoded ECG sample data for the strip.
   * @param beats - The beat markers returned for the strip.
   * @param hesBeatStatus - The per-beat HES status values returned alongside the beats.
   * @param eventsList - Rhythm events overlapping the strip, to be shaded in the viewer.
   * @param beatHeatMapIds - Heatmap ids matching each beat, used when replaying stored edits.
   * @param ecgExport - A wider ECG sample window kept for strip export.
   * @param beatTemplateIds - Template ids matching each beat, used when replaying swap edits.
   * @returns Nothing.
   */
  const createObjectEcg = (
    ecg: any,
    beats: any,
    hesBeatStatus: any,
    eventsList: any[] = [],
    beatHeatMapIds: any[] = [],
    ecgExport: any,
    beatTemplateIds: any,
  ) => {
    const { channels, gain, samplingFrequency } = ecgDataMap;
    const beatDiffSeconds = (selectedStripBeatId.current - startStripEpoch.current) / 1000;
    const beatPos = Math.round(beatDiffSeconds * samplingFrequency);
    // *: Restore data from previous editing session
    // generateUpdatedBeatsV2 doesn't declare a samplingFrequency field;
    // this call site passes one anyway and it is silently unused - pre-existing.
    const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
      beats,
      hesBeatStatus,
      beatChanges: getBeatChanges(),
      startEpoch: startStripEpoch.current,
      stopEpoch: stopStripEpoch.current,
      beatHeatMapIds,
      beatTemplateIds,
      applyBulkBeatsChanges: getBulkBeatsChanges(),
      resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      samplingFrequency: ecgDataMap.samplingFrequency,
    } as any);
    beatMarkersInEcgViewer.current = updatedBeats;
    hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;
    const events = _.map(eventsList, (event) => getEventInfoInEcgViewer(event));
    currentEcgViewerConfig.current = { ...ECG_VIEWER_CONFIG_STORE };

    const clonedEcgObject = {
      dataSamples: ecg,
      adcGain: gain,
      adcZero: 0,
      numChannels: channels.length,
      diagnosticLead: 2,
      beatPos,
      events,
      samplingRate: samplingFrequency,
      // *: Temporarily adding "aiPredict" to display beat markers from server
      aiPredict: [
        {
          beats: updatedBeats,
          hesBeatStatus: updatedHesBeatStatus,
          channel: 1,
          hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
        },
      ],
      isAppliedAIByDefault: true,
      isShowVES: true,
      isShowSVES: true,
      isShowBeatMarkers: true,
      hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
      hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
      notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
      speed: ECG_VIEWER_CONFIG_STORE.spdValue,
      amp: ECG_VIEWER_CONFIG_STORE.ampValue,
      wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
      exportEcgData: {
        ecgData: ecgExport,
      },
    };
    setEcgObject(clonedEcgObject);
    ecgObjectRef.current = clonedEcgObject;
  };

  /**
   * Rebuilds the displayed ECG object from the originally downloaded beats after edits or viewer-config changes, without refetching. Replays stored beat changes, bulk changes and resets onto the original markers and only republishes when the markers, HES statuses or shared viewer config actually differ, applying the current filter, speed and amplitude settings and resetting the chart mode when the change came from beat editing.
   *
   * @param skipCheck - Forces the rebuild even when nothing appears to have changed.
   * @param isChangeBeats - Marks the rebuild as caused by beat editing, which resets the chart interaction mode.
   * @returns Nothing.
   */
  const reCreateObjectEcg = (skipCheck: boolean = false, isChangeBeats: boolean = false) => {
    if (!_.isEmpty(ecgObject)) {
      const { updatedBeats, updatedHesBeatStatus } = generateUpdatedBeatsV2({
        beats: originalBeatMarkers.current,
        hesBeatStatus: originalHesBeatStatus.current,
        beatTemplateIds: originalBeatTemplateIds.current,
        beatChanges: getBeatChanges(),
        startEpoch: startStripEpoch.current,
        stopEpoch: stopStripEpoch.current,
        beatHeatMapIds: originalBeatHeatMapIds.current,
        applyBulkBeatsChanges: getBulkBeatsChanges(),
        resetAppliedBulkBeatsChanges: getResetBulkBeatsChanges(),
      });

      // *: Only update by editing from above beat list
      if (
        !_.isEqual(updatedBeats, beatMarkersInEcgViewer.current) ||
        !_.isEqual(updatedHesBeatStatus, hesBeatStatusInEcgViewer.current) ||
        !_.isEqual(currentEcgViewerConfig.current, ECG_VIEWER_CONFIG_STORE) ||
        skipCheck
      ) {
        beatMarkersInEcgViewer.current = updatedBeats;
        hesBeatStatusInEcgViewer.current = updatedHesBeatStatus;

        currentEcgViewerConfig.current = { ...ECG_VIEWER_CONFIG_STORE };

        const clonedEcgObject = _.cloneDeep(ecgObject);
        _.assign(clonedEcgObject, {
          hiddenChannels: ECG_VIEWER_CONFIG_STORE.visibleChannels,
          hpFilters: ECG_VIEWER_CONFIG_STORE.hiPValue,
          notchFilters: ECG_VIEWER_CONFIG_STORE.loPValue,
          speed: ECG_VIEWER_CONFIG_STORE.spdValue,
          amp: ECG_VIEWER_CONFIG_STORE.ampValue,
          wndDuration: ECG_VIEWER_CONFIG_STORE.wndDuration,
          aiPredict: [
            {
              beats: updatedBeats,
              hesBeatStatus: updatedHesBeatStatus,
              channel: 1,
              hesEventStatus: _.times(updatedHesBeatStatus.length, () => 1),
            },
          ],
        });
        if (isChangeBeats) {
          if (ecgChartRef.current) {
            ecgChartRef.current.resetInMode(true);
          }
        }
        setEcgObject(clonedEcgObject);
        ecgObjectRef.current = clonedEcgObject;
      }
    }
  };

  /**
   * Loads the ten-second ECG window and beat data centred on a beat and hands them to the viewer builder. Fetches samples and beats in parallel five seconds either side, discards the result if the selection changed meanwhile, gathers the overlapping persisted and newly created rhythm events for the covered days, derives the AI channel from the beat channels, pulls a wider export window, and logs any non-cancelled failure.
   *
   * @param beat - The beat to centre the strip on; an empty value aborts the load.
   * @param needLoading - Whether to raise and clear the local loading flag around the fetch.
   * @returns A promise resolving once the strip, its beats and the export window have been stored.
   */
  const downloadDataEcg = async (beat: any, needLoading: boolean = true) => {
    if (_.isEmpty(beat)) return;
    if (needLoading) {
      setIsLoading(true);
    }
    try {
      const offsetEvent = 5000;
      const startTime = convertToMomentJs(beat.id - offsetEvent, timezoneOffset);
      const stopTime = convertToMomentJs(beat.id + offsetEvent, timezoneOffset);
      const [ecgData, holterBeats] = await Promise.all([
        checkAndProcessEcgData({
          ecgDataMap,
          beat,
          offset: offsetEvent,
          dataLength: ecgDataMap.samplingFrequency * 10,
        }),
        checkAndProcessBeatData({
          beat,
          offset: offsetEvent,
          ecgDataMap,
        }),
      ]);

      if (isActiveQueryBeat) {
        startStripEpoch.current = startTime.valueOf();
        stopStripEpoch.current = stopTime.valueOf();
        const startTimeStartDay = startTime.clone().startOf('days');
        const stopTimeStartDay = stopTime.clone().startOf('days');
        const isBetweenDays = stopTimeStartDay.diff(startTimeStartDay, 'days');
        let currentDateCombinedEvents: any;
        if (isBetweenDays) {
          currentDateCombinedEvents = (
            combinedEventsInHeatmap[startTimeStartDay.toISOString()] || []
          ).concat(combinedEventsInHeatmap[stopTimeStartDay.toISOString()]);
        } else {
          currentDateCombinedEvents = combinedEventsInHeatmap[startTimeStartDay.toISOString()];
        }
        const eventsList: any[] = [];
        if (!_.isEmpty(currentDateCombinedEvents)) {
          _.forEach(currentDateCombinedEvents, (event: any) => {
            if (
              event.type !== TYPE_BEAT_ENUM.DELETED &&
              !!event.idEvent &&
              typesPersistEventV2.includes(event.type)
            ) {
              const isOverlap =
                new Date(event.start).getTime() <= stopStripEpoch.current &&
                new Date(event.stop) >= startStripEpoch.current;
              if (isOverlap) {
                eventsList.push(event);
              }
            }
          });
        }
        if (!_.isEmpty(eventNews)) {
          _.forEach(eventNews, (event: any) => {
            if (event.type !== TYPE_BEAT_ENUM.DELETED && typesPersistEventV2.includes(event.type)) {
              const isOverlap =
                new Date(event.start).getTime() <= stopStripEpoch.current &&
                new Date(event.stop) >= startStripEpoch.current;
              if (isOverlap) {
                eventsList.push(event);
              }
            }
          });
        }
        const channel = countBeatChannels(holterBeats?.beatChannels);
        // countBeatChannels returns a number but aiChannelState (Task 7) is typed TAiChannel | null - pre-existing.
        setAiChannel(channel as any);
        const exportStart = startStripEpoch.current - 10000;
        const exportStop = stopStripEpoch.current + 10000;
        // checkAndProcessEcgData's momentObject type (common/handlerEcgData.ts, Task 7) declares
        // string | number, but the function itself does moment(momentObject.startMoment) - it accepts Moment too.
        const ecgExport = await checkAndProcessEcgData({
          ecgDataMap,
          dataLength: ((exportStart - exportStop) / 1000) * ecgDataMap.samplingFrequency,
          momentObject: {
            startMoment: moment(exportStart),
            stopMoment: moment(exportStop),
          } as any,
        });
        createObjectEcg(
          ecgData,
          holterBeats?.beats || [],
          holterBeats?.hesBeatStatus || [],
          eventsList || [],
          holterBeats?.beatHeatMapIds || [],
          ecgExport,
          holterBeats?.beatTemplateIds || [],
        );
        originalBeatMarkers.current = holterBeats?.beats || [];
        originalHesBeatStatus.current = holterBeats?.hesBeatStatus || [];
        originalBeatHeatMapIds.current = holterBeats?.beatHeatMapIds || [];
        originalBeatTemplateIds.current = holterBeats?.beatTemplateIds || [];
        if (needLoading) {
          setIsLoading(false);
        }
      }
    } catch (error) {
      if (!_.includes(error.message, 'canceled')) {
        logError('Failed to download ECG data: ', error);
      }
      if (needLoading) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Records the timestamp the user clicked inside the strip as the start, end or bookmark date currently being picked. Does nothing unless a pick action is active, otherwise it offsets the strip start by the clicked seconds and writes the ISO date into the matching store field.
   *
   * @param diffSeconds - Seconds from the start of the displayed strip to the clicked point.
   * @returns Nothing.
   */
  const handlePickDate = (diffSeconds: number) => {
    if (isPickingDate) {
      const date = convertToMomentJs(startStripEpoch.current, timezoneOffset)
        .add(diffSeconds, 'seconds')
        .toISOString();
      switch (pickDateAction) {
        case TPickDateAction.START: {
          setPickedStartDate(date);
          break;
        }
        case TPickDateAction.END: {
          setPickedEndDate(date);
          break;
        }
        case TPickDateAction.BOOKMARK: {
          setPickedBookmarkDate(date);
          break;
        }
        default: {
          break;
        }
      }
    }
  };

  /**
   * Persists beat marker edits made inside the ECG viewer back into the tab's change lists. Diffs the incoming markers and HES statuses against the ones currently drawn, falling back to the originals when empty, folds the diff into the stored beat changes, recomputes which applied bulk beat changes are now reset, and mirrors both into the beat options used by undo and redo.
   *
   * @param updatedBeatObject - The viewer payload carrying currentBeatMarkers and currentHesBeatStatus.
   * @returns Nothing.
   */
  const handleUpdateBeat = (updatedBeatObject: any) => {
    if (!_.isEmpty(ecgObject)) {
      const { currentBeatMarkers, currentHesBeatStatus } = updatedBeatObject;

      const updatedBeatMarkers = getUpdatedBeatMarkerV2(
        currentBeatMarkers,
        _.isEmpty(beatMarkersInEcgViewer.current)
          ? originalBeatMarkers.current
          : beatMarkersInEcgViewer.current,
        currentHesBeatStatus,
        _.isEmpty(hesBeatStatusInEcgViewer.current)
          ? originalHesBeatStatus.current
          : hesBeatStatusInEcgViewer.current,
        originalHesBeatStatus.current,
        startStripEpoch.current,
        originalBeatHeatMapIds.current,
        originalBeatMarkers.current,
      );

      beatMarkersInEcgViewer.current = currentBeatMarkers;
      hesBeatStatusInEcgViewer.current = currentHesBeatStatus;

      // *: Update to aiPredict to restore data when clicking "Reset changes" from componentDidUpdate (AIEcgViewer)
      const clonedEcgObject = _.cloneDeep(ecgObject);
      clonedEcgObject.hiddenChannels = ECG_VIEWER_CONFIG_STORE.visibleChannels;

      let beatsChanges: any[] = [];
      setBeatChanges((currBeatChange: any) => {
        const updatedBeatChanges = handleUpdateBeatChanges(updatedBeatMarkers, currBeatChange);
        currentBeatsChangeRef.current = updatedBeatChanges;
        beatsChanges = updatedBeatChanges;
        return updatedBeatChanges;
      });

      const resetBulkChanges = handleResetAppliedBuldBeats({
        appliedBuldBeats: getResetBulkBeatsChanges(),
        strips: updatedBeatMarkers,
        type: updatedBeatMarkers?.[0]?.type,
        bulkBeatsChanges: getBulkBeatsChanges(),
      });
      currentRrResetChangeRef.current = resetBulkChanges;
      setResetBulkBeatsChanges(resetBulkChanges);
      setBeatOptions((prev: any) => ({
        ...prev,
        beatChangesState: beatsChanges,
        resetBulkBeatsChangesState: resetBulkChanges,
      }));
    }
  };

  /**
   * Moves the ECG viewer ten seconds forward or backward from the beat currently displayed on the RR-interval tab. Shifts the displayed beat id by ten thousand milliseconds in the chosen direction and, when the target day has no combined heatmap events cached yet, asks the parent to load the day before, the day itself and the day after before committing the new id.
   *
   * @param isNext - Whether to step forward in time; steps backward when false.
   * @returns Nothing.
   */
  const navigateStrip = (isNext: boolean = false) => {
    let clonedDisplayedBeatId = displayedBeatId;
    clonedDisplayedBeatId += isNext ? 10000 : -10000;
    const nextStartDay = convertToMomentJs(clonedDisplayedBeatId, timezoneOffset).startOf('days');
    const currentDateCombinedEvents = combinedEventsInHeatmap[nextStartDay.valueOf()];
    if (_.isEmpty(currentDateCombinedEvents)) {
      callbackListDate([
        nextStartDay.add(1, 'days').toISOString(),
        nextStartDay.toISOString(),
        nextStartDay.clone().subtract(1, 'days').toISOString(),
      ]);
    }
    setDisplayedBeatId(clonedDisplayedBeatId);
  };

  const handleCaptureImages = ({
    draw,
    allIndexStripSvg,
    technicianComments,
    captureMode,
    captureTitle,
    visibleChannels,
  }: {
    draw: any;
    allIndexStripSvg: any;
    technicianComments: any;
    captureMode: any;
    captureTitle: any;
    visibleChannels: any;
  }) => {
    try {
      if (allIndexStripSvg.length > 0 && captureMode === CAPTURE_MODE.OTHER) {
        const { samplingFrequency } = ecgDataMap;
        const { formatDraw, stripStart, stripStop } = formatDrawMutation(
          draw,
          samplingFrequency,
          startStripEpoch.current,
          timezoneOffset,
          visibleChannels,
        );
        const startTime = stripStart;
        const endTime = stripStop;

        // handleAddEventOthersV2 declares a required miniteInfo field this call site never
        // passes - pre-existing (some sibling wrappers pass getMinuteInfoEvents(), this one doesn't).
        const data = handleAddEventOthersV2({
          events: getEventOthers(),
          draw: formatDraw,
          allIndexStripSvg,
          technicianComments,
          captureTitle,
          startTime,
          endTime,
        } as any);
        // Perform grouping event action applicable to undo/redo
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventOthersState: data,
        }));
        setEventOthers(data);
      }
    } catch (error) {
      logError('Failed to capture images: ', error);
      toastrError(error.message, 'Error');
    }
  };

  const eventIndicator = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    let startPos = null;
    let endPos = null;
    if (pickedStartDate) {
      const pickedStartEpoch = new Date(pickedStartDate).getTime();
      startPos = Math.round(
        ((pickedStartEpoch - startStripEpoch.current) / 1000) * samplingFrequency,
      );
    }
    if (pickedEndDate) {
      const pickedEndEpoch = new Date(pickedEndDate).getTime();
      endPos = Math.round(((pickedEndEpoch - startStripEpoch.current) / 1000) * samplingFrequency);
    }
    return {
      startPos,
      endPos,
    };
  }, [pickedStartDate, pickedEndDate, ecgObject]);

  const bookmarksInRange = useMemo(() => {
    const result: any[] = [];
    _.forEach(ecgBookmarks, (bookmark: any) => {
      if (bookmark.center >= startStripEpoch.current && bookmark.center <= stopStripEpoch.current) {
        result.push(bookmark);
      }
    });
    return result;
  }, [ecgBookmarks, ecgObject]);

  const bookmarkIndicators = useMemo(() => {
    const { samplingFrequency } = ecgDataMap;
    const listBookmarks: any[] = [];
    if (pickedBookmarkDate) {
      const pickedEpoch = new Date(pickedBookmarkDate).getTime();
      listBookmarks.push({
        sample: Math.round(((pickedEpoch - startStripEpoch.current) / 1000) * samplingFrequency),
        id: 'marker',
      });
    }
    _.forEach(bookmarksInRange, (bookmark: any) => {
      listBookmarks.push({
        sample: Math.round(
          ((bookmark.center - startStripEpoch.current) / 1000) * samplingFrequency,
        ),
        id: bookmark.center,
      });
    });
    return listBookmarks;
  }, [pickedBookmarkDate, bookmarksInRange, ecgObject]);

  useEffect(() => {
    /**
     * Cancels an in-progress date pick when the user presses Escape over the strip. Ignores auto-repeat keyups and only clears the pick action when one is currently active.
     *
     * @param event - The keyup event, whose key and repeat flag are inspected.
     * @returns Nothing.
     */
    const handleKeyup = (event: any) => {
      if (event?.repeat) {
        return;
      }
      const { key } = event;
      if (key === 'Escape' && pickDateAction) {
        setPickDateAction(TPickDateAction.NONE);
      }
    };
    if (isPickingDate) {
      window.addEventListener('keyup', handleKeyup);
    }
    return () => {
      window.removeEventListener('keyup', handleKeyup);
    };
  }, [pickDateAction, isPickingDate]);

  useLayoutEffect(() => {
    setDisplayedBeatId(selectedStrip?.id);
    selectedStripBeatId.current = selectedStrip?.id;
  }, [selectedStrip?.id]);

  useEffect(() => {
    isActiveQueryBeat = true;
    if (displayedBeatId && isRenderViewerEcg) {
      downloadDataEcg({ id: displayedBeatId });
    }
    return () => {
      isActiveQueryBeat = false;
    };
  }, [displayedBeatId, combinedEventsInHeatmap, reloadEcgViewer, isRenderViewerEcg, eventNews]);

  useUpdateEffect(() => {
    if (!_.isEqual(beatChanges, currentBeatsChangeRef.current)) {
      currentBeatsChangeRef.current = beatChanges;
      reCreateObjectEcg(false, true);
    }
  }, [beatChanges]);

  useUpdateEffect(() => {
    if (!_.isEqual(currentRrResetChangeRef.current, resetBulkBeatsChanges)) {
      currentRrResetChangeRef.current = resetBulkBeatsChanges;
      reCreateObjectEcg();
    }
  }, [resetBulkBeatsChanges]);

  useUpdateEffect(() => {
    reCreateObjectEcg();
  }, [bulkBeatsChanges]);

  useUpdateEffect(() => {
    if (isActiveTab) {
      if (!_.isEqual(currentEcgViewerConfig.current, ECG_VIEWER_CONFIG_STORE)) {
        reCreateObjectEcg(true);
      }
    } else {
      reCreateObjectEcg(true);
    }
    if (ecgChartRef.current && isActiveTab) {
      ecgChartRef.current.resetInMode();
    }
  }, [isActiveTab]);

  /**
   * Refreshes the annotation overlay of the displayed strip after the tab's other events change. Flattens the draw shapes off every other event, converts them to viewer coordinates against the strip start, stop and sampling frequency, and republishes the cloned ECG object with the new widget list.
   *
   * @returns Nothing.
   */
  const reDrawEcgViewer = () => {
    if (!_.isEmpty(ecgObject)) {
      const clonedEcgObject = _.cloneDeep(ecgObjectRef.current);
      const { samplingFrequency } = ecgDataMap;
      /**
       * Collects every drawing shape attached to the tab's other events so the viewer can re-render its annotation overlay. Flattens the draw payload of each other event and drops falsy entries, defaulting to an empty array.
       *
       * @returns The flattened list of non-empty draw shapes.
       */
      const drawList =
        _.filter(_.flatten(_.map(otherEvents, (event) => event.draw)), (x) => !!x) || [];
      const { draw } = formatDrawDisplay(
        drawList,
        startStripEpoch.current,
        samplingFrequency,
        stopStripEpoch.current,
      );
      const viewerObject = {
        options: {},
        widgets: draw,
      };
      _.assign(clonedEcgObject, {
        viewerObject,
      });
      setEcgObject(clonedEcgObject);
      ecgObjectRef.current = clonedEcgObject;
    }
  };

  useUpdateEffect(() => {
    reDrawEcgViewer();
  }, [otherEvents]);

  useUpdateEffect(() => {
    /**
     * Moves the ECG window so a requested jump timestamp becomes visible on the RR-interval tab. Clears the jump request outright when the target already falls inside the loaded strip, otherwise it recomputes the surrounding range, centres the displayed beat id on it, requests the neighbouring days of heatmap events when that day is not cached, and then clears the jump date and portion.
     *
     * @returns Nothing.
     */
    const handleJumpToEcgViewer = () => {
      const convertJumpDate = moment(jumpDate);
      const epochJumpDate = convertJumpDate.valueOf();
      if (epochJumpDate <= stopStripEpoch.current && epochJumpDate >= startStripEpoch.current) {
        setJumpDate(undefined);
        setJumpPortion(0);
        return;
      }
      const [newStart, newEnd] = calculateNewRange(startStripEpoch.current, epochJumpDate);
      const newDisplayedBeatId = newStart + Math.round((newEnd - newStart) / 2);
      const newStartDay = convertToMomentJs(newDisplayedBeatId, timezoneOffset).startOf('days');
      const currentDateCombinedEvents = combinedEventsInHeatmap[newStartDay.valueOf()];
      if (_.isEmpty(currentDateCombinedEvents)) {
        callbackListDate([
          newStartDay.clone().add(1, 'days').toISOString(),
          newStartDay.toISOString(),
          newStartDay.clone().subtract(1, 'days').toISOString(),
        ]);
      }
      setDisplayedBeatId(newDisplayedBeatId);
      setJumpDate(undefined);
      setJumpPortion(0);
    };
    if (jumpDate && startStripEpoch.current && stopStripEpoch.current) {
      handleJumpToEcgViewer();
    }
  }, [
    jumpDate,
    ecgObject, // trigger useEffect when data has cached
  ]);

  const disabledNoDataRange = useMemo(() => {
    let isDisabledNext = true;
    let isDisabledPrev = true;
    const nextDisplayBeatId = displayedBeatId + 10000;
    const prevDisplayBeatId = displayedBeatId - 10000;
    _.forEach(ecgDataMap.data, (range) => {
      if (nextDisplayBeatId >= range.start && nextDisplayBeatId <= range.stop) {
        isDisabledNext = false;
      }
      if (prevDisplayBeatId >= range.start && prevDisplayBeatId <= range.stop) {
        isDisabledPrev = false;
      }
    });
    return { isDisabledPrev, isDisabledNext };
  }, [ecgDataMap, displayedBeatId]);

  return {
    isLoading,
    isLoadingStrip,
    ecgObject,
    startStripEpoch,
    loadingBeatStatus,
    selectedStrip,
    displayedBeatId,
    navigateStrip,
    disabledNoDataRange,
    ecgChartRef,
    aiChannel,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    handleCaptureImages,
    handlePickDate,
    handleUpdateBeat,
  };
};

export default useBeatEcgViewerWrapper;
